import { useState } from 'react'
import { toast } from 'react-hot-toast'
import type { Producto, Deposito } from '@/types/database'
import { useAjustarStock, getStockTotal } from './hooks'
import { useAuth } from '@/features/auth/AuthProvider'

interface Props {
  producto: Producto
  depositos: Deposito[]
  onClose: () => void
}

export default function AjusteStockModal({ producto, depositos, onClose }: Props) {
  const { user } = useAuth()
  const ajustar = useAjustarStock()

  const depositoDefault = depositos[0]?.id ?? ''
  const [depositoId, setDepositoId] = useState(depositoDefault)
  const [tipo, setTipo] = useState<'entrada' | 'salida' | 'ajuste'>('entrada')
  const [cantidad, setCantidad] = useState('')
  const [notas, setNotas] = useState('')
  const [numeroLote, setNumeroLote] = useState('')
  const [fechaVencimiento, setFechaVencimiento] = useState('')

  const stockActual = getStockTotal(producto)

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    const cant = parseInt(cantidad)
    if (!cant || cant <= 0) {
      toast.error('Ingresa una cantidad valida')
      return
    }
    if (!depositoId) {
      toast.error('Selecciona un deposito')
      return
    }
    try {
      await ajustar.mutateAsync({
        productoId: producto.id,
        depositoId,
        tipo,
        cantidad: cant,
        notas,
        usuarioId: user!.id,
        numero_lote: numeroLote || undefined,
        fecha_vencimiento: fechaVencimiento || undefined,
      })
      toast.success('Stock actualizado')
      onClose()
    } catch (err: unknown) {
      toast.error(err instanceof Error ? err.message : 'Error al ajustar stock')
    }
  }

  return (
    <div
      style={{
        position: 'fixed', inset: 0, zIndex: 50,
        background: 'rgba(0,0,0,0.35)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 16,
      }}
      onClick={e => { if (e.target === e.currentTarget) onClose() }}
    >
      <div style={{
        background: 'var(--me-surface)',
        borderRadius: 'var(--me-r-lg)',
        width: '100%', maxWidth: 420,
        padding: 28,
        boxShadow: '0 20px 60px rgba(0,0,0,0.18)',
      }}>
        <div style={{ marginBottom: 20 }}>
          <div style={{
            fontFamily: 'var(--me-display)',
            fontVariationSettings: '"opsz" 24, "wght" 700',
            fontSize: 18,
            letterSpacing: '-0.025em',
          }}>
            Ajuste de stock
          </div>
          <div style={{ fontSize: 13, color: 'var(--me-text-2)', marginTop: 2 }}>
            {producto.nombre} — actual: <strong>{stockActual}</strong> unidades
          </div>
        </div>

        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          {depositos.length > 1 && (
            <div>
              <label className="me-label">Deposito</label>
              <select
                className="me-input"
                style={{ width: '100%' }}
                value={depositoId}
                onChange={e => setDepositoId(e.target.value)}
              >
                {depositos.map(d => (
                  <option key={d.id} value={d.id}>{d.nombre}</option>
                ))}
              </select>
            </div>
          )}

          <div>
            <label className="me-label">Tipo de movimiento</label>
            <div style={{ display: 'flex', gap: 8 }}>
              {(['entrada', 'salida', 'ajuste'] as const).map(t => (
                <button
                  key={t}
                  type="button"
                  onClick={() => setTipo(t)}
                  style={{
                    flex: 1,
                    padding: '8px 0',
                    borderRadius: 'var(--me-r-btn)',
                    border: '1px solid',
                    fontSize: 13,
                    fontWeight: 500,
                    cursor: 'pointer',
                    textTransform: 'capitalize',
                    transition: 'all 0.12s',
                    borderColor: tipo === t ? 'var(--me-acid)' : 'var(--me-line-2)',
                    background: tipo === t ? 'var(--me-acid)' : 'var(--me-surface)',
                    color: tipo === t ? 'var(--me-acid-ink)' : 'var(--me-text)',
                  }}
                >
                  {t}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="me-label">
              {tipo === 'ajuste' ? 'Stock nuevo total' : 'Cantidad'}
            </label>
            <input
              className="me-input"
              style={{ width: '100%' }}
              type="number"
              min="0"
              placeholder={tipo === 'ajuste' ? 'Nuevo total en stock' : 'Cantidad a mover'}
              value={cantidad}
              onChange={e => setCantidad(e.target.value)}
              autoFocus
            />
          </div>

          <div>
            <label className="me-label">Notas (opcional)</label>
            <input
              className="me-input"
              style={{ width: '100%' }}
              type="text"
              placeholder="Motivo del ajuste..."
              value={notas}
              onChange={e => setNotas(e.target.value)}
            />
          </div>

          {tipo === 'entrada' && (
            <>
              <div>
                <label className="me-label">N° de lote (opcional)</label>
                <input
                  className="me-input"
                  style={{ width: '100%', fontFamily: 'var(--me-mono)', fontSize: 13 }}
                  type="text"
                  placeholder="Ej: L-2024-001"
                  value={numeroLote}
                  onChange={e => setNumeroLote(e.target.value)}
                />
              </div>
              <div>
                <label className="me-label">Fecha de vencimiento (opcional)</label>
                <input
                  className="me-input"
                  style={{ width: '100%', fontFamily: 'var(--me-mono)', fontSize: 13 }}
                  type="date"
                  value={fechaVencimiento}
                  onChange={e => setFechaVencimiento(e.target.value)}
                />
              </div>
            </>
          )}

          <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
            <button
              type="button"
              className="me-btn me-btn-ghost"
              style={{ flex: 1 }}
              onClick={onClose}
            >
              Cancelar
            </button>
            <button
              type="submit"
              className="me-btn me-btn-primary"
              style={{ flex: 2 }}
              disabled={ajustar.isPending}
            >
              {ajustar.isPending ? 'Guardando...' : 'Confirmar ajuste'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
